/**
 * useVatValidation Hook
 * React Query hooks for VAT validation operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  validateVat,
  getVatValidationHistory,
  acceptVatValidation,
  checkIntraCommunity,
  checkPeppolRegistration,
  refreshPeppolRegistration,
  getPeppolCache
} from '../api/facturation.api';

// Query keys
export const vatKeys = {
  all: ['vat'] as const,
  validation: (vatNumber: string) => [...vatKeys.all, 'validation', vatNumber] as const,
  history: (vatNumber: string) => [...vatKeys.all, 'history', vatNumber] as const,
  intraCommunity: (sender: string, receiver: string) => [...vatKeys.all, 'intra-community', sender, receiver] as const,
  peppol: (vatNumber: string) => [...vatKeys.all, 'peppol', vatNumber] as const,
  peppolCache: (registered?: boolean) => [...vatKeys.all, 'peppol-cache', registered] as const,
};

// Throttle utility for VIES API rate limiting (1 request per second)
let lastVatCall = 0;
const VAT_THROTTLE_MS = 1000;

const throttledValidateVat = async (
  vatNumber: string,
  storedName?: string,
  storedAddress?: string
) => {
  const now = Date.now();
  const timeSinceLastCall = now - lastVatCall;

  if (timeSinceLastCall < VAT_THROTTLE_MS) {
    // Wait until we can make the next call
    await new Promise(resolve => setTimeout(resolve, VAT_THROTTLE_MS - timeSinceLastCall));
  }

  lastVatCall = Date.now();
  return validateVat(vatNumber, storedName, storedAddress);
};

// Validate VAT number
export const useValidateVat = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ vatNumber, storedName, storedAddress }: {
      vatNumber: string;
      storedName?: string;
      storedAddress?: string;
    }) => throttledValidateVat(vatNumber, storedName, storedAddress),
    onSuccess: (data) => {
      // Update cache with validation result
      queryClient.setQueryData(vatKeys.validation(data.vatNumber), data);
      // Invalidate history
      queryClient.invalidateQueries({ queryKey: vatKeys.history(data.vatNumber) });
    },
  });
};

// Get VAT validation history
export const useVatValidationHistory = (vatNumber: string, limit = 10) => {
  return useQuery({
    queryKey: vatKeys.history(vatNumber),
    queryFn: () => getVatValidationHistory(vatNumber, limit),
    enabled: !!vatNumber && vatNumber.length >= 9,
    staleTime: 60000, // 1 minute
  });
};

// Accept VAT validation (use official data)
export const useAcceptVatValidation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (validationId: number) => acceptVatValidation(validationId),
    onSuccess: () => {
      // Invalidate all VAT queries
      queryClient.invalidateQueries({ queryKey: vatKeys.all });
    },
  });
};

// Check intra-community VAT
export const useCheckIntraCommunity = (senderVatNumber: string, receiverVatNumber: string) => {
  return useQuery({
    queryKey: vatKeys.intraCommunity(senderVatNumber, receiverVatNumber),
    queryFn: () => checkIntraCommunity(senderVatNumber, receiverVatNumber),
    enabled: !!senderVatNumber && !!receiverVatNumber,
    staleTime: 300000, // 5 minutes
  });
};

// Check Peppol registration
export const usePeppolRegistration = (vatNumber: string) => {
  return useQuery({
    queryKey: vatKeys.peppol(vatNumber),
    queryFn: () => checkPeppolRegistration(vatNumber),
    enabled: !!vatNumber && vatNumber.length >= 9,
    staleTime: 86400000, // 24 hours (cached on backend too)
  });
};

// Refresh Peppol registration
export const useRefreshPeppolRegistration = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (vatNumber: string) => refreshPeppolRegistration(vatNumber),
    onSuccess: (data) => {
      queryClient.setQueryData(vatKeys.peppol(data.vatNumber), data);
    },
  });
};

// Get Peppol cache
export const usePeppolCache = (registered?: boolean, limit = 100) => {
  return useQuery({
    queryKey: vatKeys.peppolCache(registered),
    queryFn: () => getPeppolCache(registered, limit),
    staleTime: 60000, // 1 minute
  });
};
